import React from 'react';
import { Signal } from '@/api/types';
import { SignalCard } from './SignalCard';
import { LoadingOverlay, EmptyState, ErrorMessage } from '@/components/common';

interface SignalListProps {
  signals: Signal[];
  currentPrices: Record<string, number>;
  isLoading: boolean;
  error: string | null;
  onSignalClick: (signal: Signal) => void;
  onCreateClick: () => void;
  onRetry: () => void;
}

export const SignalList: React.FC<SignalListProps> = ({
  signals,
  currentPrices,
  isLoading,
  error,
  onSignalClick,
  onCreateClick,
  onRetry,
}) => {
  if (isLoading && signals.length === 0) {
    return <LoadingOverlay message="Loading your signals..." />;
  }

  if (error && signals.length === 0) {
    return <ErrorMessage message={error} onRetry={onRetry} />;
  }

  if (signals.length === 0) {
    return (
      <EmptyState
        icon="🔔"
        title="No Price Alerts"
        description="Create your first price alert to get notified when crypto prices change."
        actionLabel="Create Alert"
        onAction={onCreateClick}
      />
    );
  }

  // Group signals: active first, then inactive
  const activeSignals = signals.filter((s) => s.active);
  const inactiveSignals = signals.filter((s) => !s.active);

  return (
    <div style={{ padding: '16px' }}>
      {activeSignals.length > 0 && (
        <>
          <h2
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--tg-theme-section-header-text-color)',
              textTransform: 'uppercase',
              marginBottom: '12px',
              paddingLeft: '4px',
            }}
          >
            Active ({activeSignals.length})
          </h2>
          {activeSignals.map((signal) => (
            <SignalCard
              key={signal.id}
              signal={signal}
              currentPrice={currentPrices[signal.id]}
              onClick={() => onSignalClick(signal)}
            />
          ))}
        </>
      )}

      {inactiveSignals.length > 0 && (
        <>
          <h2
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--tg-theme-section-header-text-color)',
              textTransform: 'uppercase',
              marginTop: activeSignals.length > 0 ? '24px' : 0,
              marginBottom: '12px',
              paddingLeft: '4px',
            }}
          >
            Paused ({inactiveSignals.length})
          </h2>
          {inactiveSignals.map((signal) => (
            <SignalCard
              key={signal.id}
              signal={signal}
              currentPrice={currentPrices[signal.id]}
              onClick={() => onSignalClick(signal)}
            />
          ))}
        </>
      )}
    </div>
  );
};
